import { useEffect, useState } from "react";
import NextIcon from "Assets/Img/NextIcon";
import Button from "../../../../Components/Common/Button/Button";
import Skeleton from "../../../../Components/Common/Skeleton/Skeleton";
import { useToast } from "../../../../Components/Common/Toast/ToastContext";
import {
  getGcProgress,
  updateGcProgress,
} from "../../../../services/projectService";
import NoData from "Pages/Admin/InspectorAssign/components/NoData";

export type ProgressStatus =
  | "not-started"
  | "in-progress"
  | "nearing"
  | "complete";

export interface ProgressItem {
  id: string;
  description: string;
  subtext: string;
  status: ProgressStatus;
}

interface ConstructionProgressStepProps {
  projectId: string;
  onNext: () => void;
  drawNumber: string;
  bankCompanyName: string;
}

const statusLabel: Record<ProgressStatus, string> = {
  "not-started": "Not Started",
  "in-progress": "In Progress",
  nearing: "Nearing Completion",
  complete: "Complete",
};

const fromApiStatus = (s: string | null | undefined): ProgressStatus => {
  if (s === "in_progress") return "in-progress";
  if (s === "nearing_completion") return "nearing";
  if (s === "complete" || s === "completed") return "complete";
  return "not-started";
};

const toApiStatus = (s: ProgressStatus): string => {
  if (s === "not-started") return "not_started";
  if (s === "in-progress") return "in_progress";
  if (s === "nearing") return "nearing_completion";
  return "completed";
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const normalizeItem = (raw: any, index: number): ProgressItem => {
  const lien = raw.lienItem ?? {};
  const aia = raw.aiaItem ?? {};
  const description = aia.itemName ?? lien.description ?? "";
  const subtextParts = [
    aia.itemCode != null ? String(aia.itemCode) : null,
    lien.subContractor?.companyName ?? null,
    lien.amount?.amount != null
      ? `SOV: $${Number(lien.amount.amount).toLocaleString()}`
      : null,
  ].filter(Boolean);
  return {
    id: raw.id ?? String(index),
    description,
    subtext: subtextParts.join(" — "),
    status: fromApiStatus(raw.status),
  };
};

const ConstructionProgressStep = ({
  projectId,
  onNext,
  drawNumber,
  bankCompanyName,
}: ConstructionProgressStepProps) => {
  const showToast = useToast();
  const [items, setItems] = useState<ProgressItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!projectId) return;
    setLoading(true);
    getGcProgress(projectId)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .then((res: any) => {
        const raw = Array.isArray(res?.data?.data?.items)
          ? res.data.data.items
          : Array.isArray(res?.data?.data)
            ? res.data.data
            : Array.isArray(res?.data)
              ? res.data
              : [];
        setItems(raw.map(normalizeItem));
      })
      .catch(() => showToast("Failed to load progress items"))
      .finally(() => setLoading(false));
  }, [projectId]);

  type StatusCounts = Record<ProgressStatus, number>;
  const counts = items.reduce<StatusCounts>(
    (acc, item) => {
      acc[item.status] += 1;
      return acc;
    },
    { "not-started": 0, "in-progress": 0, nearing: 0, complete: 0 },
  );

  const updateStatus = (id: string, status: ProgressStatus) =>
    setItems((prev) =>
      prev.map((item) => (item.id === id ? { ...item, status } : item)),
    );

  const setAllStatuses = (status: ProgressStatus) =>
    setItems((prev) => prev.map((item) => ({ ...item, status })));

  const handleNext = async () => {
    if (saving) return;
    setSaving(true);
    try {
      const payload = items.map((item) => ({
        id: item.id,
        status: toApiStatus(item.status),
      }));
      await updateGcProgress(projectId, payload, true);
      onNext();
    } catch {
      showToast("Failed to save progress. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <section
      className="screen activeScreen"
      aria-labelledby="construction-progress-title"
    >
      <h2 className="sectionTitle" id="construction-progress-title">
        Construction Progress Update
      </h2>
      <p className="sectionSub">
        Report your current status for each line item in the Schedule of Values.
        This is your self-reported progress for Draw #{drawNumber} — CSC will
        verify during field inspection. Be as accurate as possible.
      </p>

      <section className="drawCard">
        <header className="drawCardHeader">
          <h3 className="drawCardTitle">SOV Line Item Status</h3>
          {!loading && items.length > 0 && (
            <div className="quickSetWrap">
              <span>{"Quick-set all:".toUpperCase()}</span>
              <Button
                type="button"
                className="quickSetBtn"
                onClick={() => setAllStatuses("not-started")}
              >
                Not Started
              </Button>
              <Button
                type="button"
                className="quickSetBtn amber"
                onClick={() => setAllStatuses("in-progress")}
              >
                In Progress
              </Button>
            </div>
          )}
        </header>

        {loading ? (
          <div>
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="progItem">
                <Skeleton width={28} height={28} borderRadius={4} />
                <div className="progInfo">
                  <Skeleton
                    width="55%"
                    height={13}
                    style={{ marginBottom: 6 }}
                  />
                  <Skeleton
                    width="40%"
                    height={11}
                    style={{ marginBottom: 10 }}
                  />
                  <div style={{ display: "flex", gap: 8 }}>
                    {Array.from({ length: 5 }).map((__, j) => (
                      <Skeleton
                        key={j}
                        width={90}
                        height={28}
                        borderRadius={6}
                      />
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : items.length === 0 ? (
          <NoData text="No progress items found." />
        ) : (
          <div>
            {items.map((item, index) => (
              <div key={item.id} className="progItem">
                <span className="progNum">{index + 1}</span>
                <div className="progInfo">
                  <p className="progDesc">{item.description}</p>
                  <p className="progSub">{item.subtext}</p>
                </div>
                <div className="progBtns">
                  {(Object.keys(statusLabel) as ProgressStatus[]).map(
                    (status) => (
                      <Button
                        type="button"
                        key={status}
                        className={`psb ${item.status === status ? `sel-${status}` : ""}`.trim()}
                        onClick={() => updateStatus(item.id, status)}
                      >
                        {statusLabel[status]}
                      </Button>
                    ),
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {!loading && items.length > 0 && (
          <footer className="progSummary">
            <span>
              <i className="psDot psDot--ns" />
              {counts["not-started"]} Not Started
            </span>
            <span>
              <i className="psDot psDot--ip" />
              {counts["in-progress"]} In Progress
            </span>
            <span>
              <i className="psDot psDot--nc" />
              {counts.nearing} Nearing Completion
            </span>
            <span>
              <i className="psDot psDot--c" />
              {counts.complete} Complete
            </span>
          </footer>
        )}
        <p className="belowNote">
          All statuses are GC self-report. CSC field inspection verifies.
        </p>
      </section>
      <div className="bannerNote">
        <span>
          {" "}
          <b>Note:</b> These statuses are your self-reported progress and will
          be visible to CSC New England and {bankCompanyName} alongside this
          draw request. CSC's field inspector will independently verify progress
          during the inspection triggered by this draw submission. Differences
          between GC-reported and CSC-verified status are noted in the progress
          report.
        </span>
      </div>

      <div className="btnRow spaced">
        <div />
        <Button
          className="btn btnPrimary"
          onClick={handleNext}
          disabled={saving || loading}
        >
          {saving ? (
            "Saving..."
          ) : (
            <>
              Next: Draw Details <NextIcon />
            </>
          )}
        </Button>
      </div>
    </section>
  );
};

export default ConstructionProgressStep;
